import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Users, CreditCard, QrCode, LogOut, 
  BarChart3, Settings, FileText, Activity,
  Search, RefreshCw, Phone, MessageCircle, 
  ScanLine, Mail, Clock, Filter, Eye
} from "lucide-react";

const API = '/api';

// MASTER ADMIN V2 §10 — descripciones legibles. El JSON crudo sigue disponible
// en el panel de detalles técnicos de cada fila.
const EVENT_LABEL = {
  qr_scan: "QR escaneado",
  qr_pdf_download: "Sticker descargado en PDF",
  call_initiated: "Llamada iniciada",
  conference_call_initiated: "Llamada en conferencia iniciada",
  callback_call_initiated: "Devolución de llamada iniciada",
  call_pin_generated: "PIN de llamada generado",
  call_connected: "Llamada conectada",
  call_completed: "Llamada finalizada",
  whatsapp_direct_link: "Enlace de WhatsApp abierto",
  whatsapp_session_created: "Conversación de WhatsApp creada",
  whatsapp_session_expired: "Conversación de WhatsApp caducada",
  sms_sent: "SMS enviado",
  sms_received: "SMS recibido",
  internal_message_sent: "Mensaje guardado para el dueño",
  message: "Mensaje",
  payment_delivery_executed: "Entrega de QR ejecutada tras el pago",
  payment_submitted: "Comprobante enviado por el cliente",
  payment_approved: "Comprobante aprobado",
  payment_rejected: "Comprobante rechazado",
  membership_activated: "Membresía activada",
  coupon_redeemed: "Cupón canjeado",
  partner_commission_changed: "Comisión de socio actualizada",
  admin_login: "Inicio de sesión de administrador",
  admin_action: "Acción de administrador",
  security_intervention: "Intervención de seguridad",
  geo_fallback_used: "País deducido por respaldo",
  call: "Llamada",
  whatsapp: "WhatsApp",
};

const eventLabel = (t) => EVENT_LABEL[t] || (t ? t.replace(/_/g, " ") : "Evento sin clasificar");

const AdminLogs = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [eventType, setEventType] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    fetchStats();
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/activity-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = {};
      if (eventType) params.event_type = eventType;
      if (vehicleId) params.vehicle_id = vehicleId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await axios.get(`${API}/admin/activity-logs`, { params });
      setLogs(response.data);
    } catch (error) {
      toast.error("Error al cargar logs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleFilter = () => {
    setLoading(true);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setEventType("");
    setVehicleId("");
    setDateFrom("");
    setDateTo("");
    setLoading(true);
    setTimeout(() => fetchLogs(), 100);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLogs();
    fetchStats();
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getEventIcon = (type) => {
    if (type?.includes("scan")) return <ScanLine className="w-4 h-4 text-blue-400" />;
    if (type?.includes("call")) return <Phone className="w-4 h-4 text-[#5BC0FF]" />;
    if (type?.includes("whatsapp")) return <MessageCircle className="w-4 h-4 text-green-400" />;
    if (type?.includes("message")) return <Mail className="w-4 h-4 text-purple-400" />;
    return <Activity className="w-4 h-4 text-slate-400" />;
  };

  const getEventBadgeColor = (type) => {
    if (type?.includes("scan")) return "bg-blue-500/20 text-blue-400";
    if (type?.includes("call_connected")) return "bg-[#1B84FF]/20 text-[#5BC0FF]";
    if (type?.includes("call_pin")) return "bg-amber-500/20 text-amber-400";
    if (type?.includes("call_failed")) return "bg-red-500/20 text-red-400";
    if (type?.includes("whatsapp")) return "bg-green-500/20 text-green-400";
    if (type?.includes("message")) return "bg-purple-500/20 text-purple-400";
    return "bg-slate-500/20 text-slate-400";
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="bg-[#14375F] border-white/15">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{value || 0}</p>
            <p className="text-sm text-slate-400">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#0B2545]">
      {/* Sidebar */}


      {/* Mobile Header */}
      

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-6xl mx-auto min-w-0">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Registro de actividad
              </h1>
              <p className="text-slate-400 mt-1">Registro detallado de escaneos, llamadas y mensajes</p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-600 text-slate-300 hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid min-w-0 grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard icon={ScanLine} label="Escaneos QR" value={stats.scans} color="bg-blue-500" />
              <StatCard icon={Phone} label="Llamadas" value={stats.calls} color="bg-[#1B84FF]" />
              <StatCard icon={MessageCircle} label="WhatsApp" value={stats.whatsapp} color="bg-green-500" />
              <StatCard icon={Mail} label="Mensajes" value={stats.internal_messages} color="bg-purple-500" />
            </div>
          )}

          {/* Filters */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Filter className="w-4 h-4" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-5 gap-4">
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Tipo de evento" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="qr_scan">Escaneo QR</SelectItem>
                    <SelectItem value="call">Llamadas</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="message">Mensajes</SelectItem>
                  </SelectContent>
                </Select>
                
                <Input
                  placeholder="ID de vehículo"
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
                
                <Input
                  type="date"
                  placeholder="Desde"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                
                <Input
                  type="date"
                  placeholder="Hasta"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                
                <div className="flex gap-2">
                  <Button onClick={handleFilter} className="flex-1 bg-[#1B84FF] hover:bg-[#0F6FDB]">
                    <Search className="w-4 h-4 mr-2" />
                    Filtrar
                  </Button>
                  <Button variant="outline" onClick={handleClearFilters} className="border-slate-600 text-slate-400">
                    Limpiar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card className="bg-[#14375F] border-white/15">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No hay logs que mostrar</p>
                </div>
              ) : (
                <div className="w-full max-w-full overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/15 hover:bg-white/10">
                        <TableHead className="text-slate-400">Evento</TableHead>
                        <TableHead className="text-slate-400">Vehículo</TableHead>
                        <TableHead className="text-slate-400">Detalles</TableHead>
                        <TableHead className="text-slate-400">Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log, index) => (
                        <TableRow key={log.id || index} className="border-white/15 hover:bg-slate-750">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getEventIcon(log.event_type)}
                              <Badge className={getEventBadgeColor(log.event_type)}>
                                {eventLabel(log.event_type || log.type)}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {log.vehicle_name || log.vehicle_id?.substring(0, 8) || '-'}
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm max-w-xs">
                            {log.details && Object.keys(log.details).length > 0 ? (
                              <details data-testid={`log-details-${log.id || index}`}>
                                <summary className="cursor-pointer text-slate-300 hover:text-white">
                                  Ver detalles técnicos
                                </summary>
                                <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-black/40 p-2 text-[10px] leading-relaxed text-slate-300">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </details>
                            ) : (
                              <span>{log.target_type || "—"}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-300">
                            <div className="flex items-center gap-1 text-sm">
                              <Clock className="w-3 h-3 text-slate-500" />
                              {log.created_at 
                                ? new Date(log.created_at).toLocaleString('es-PE', { 
                                    dateStyle: 'short', 
                                    timeStyle: 'short' 
                                  }) 
                                : '-'
                              }
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminLogs;
